import { ApolloError } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useCheckoutEventLogging } from '@fhs/cart/src/hooks/use-checkout-event-logging';
import { useCartActions, useCartValues } from '@fhs/cart/src/state/cart-store';
import {
  shouldOfferBeRemoved,
  shouldOfferV2BeRemoved,
} from '@fhs/cart/src/state/cart-store/loyalty-helpers';
import { useBoxRef } from '@fhs/utils';
import { ICartEntry } from '@rbi-ctg/menu';
import useAuth from 'hooks/auth/use-auth';
import { usePriceOrder } from 'hooks/price-order';
import { mapEntryTypeToOfferType } from 'state/cart/helpers';
import { useCRMEventsContext } from 'state/crm-events';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { isLoyaltyOfferV2 } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useOffersContext } from 'state/offers';
import { ServiceMode, useOrderContext } from 'state/order';
import useAlertOrderCateringMinimum from 'state/order/hooks/use-alert-order-catering-min';
import { useStoreContext } from 'state/store';
import { createCartEntry } from 'utils/cart';
import { redeemReward } from 'utils/cart/redeem-reward';
import { GraphQLErrorCodes, graphQLErrorsIncludeExtensionCode } from 'utils/errors';
import { useFlag } from 'utils/launchdarkly';
import { isCatering, isDelivery } from 'utils/service-mode';

import { useSanitizedRewards } from './hooks/use-sanitized-rewards';
import type { ICartContext, ICartContextArgs, ITryPricingOverrides } from './types';

const RATE_LIMIT_TIMEOUT = 3 * 60 * 1000;

const CartContext = createContext<ICartContext>({} as ICartContext);

export const CartProvider: React.FC<React.PropsWithChildren<ICartContextArgs>> = ({ children }) => {
  const { locale: customerLocale } = useLocale();
  const { user, refreshCurrentUser } = useAuth();
  const { formatMessage } = useIntl();

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const checkoutCateringPriceMinimum = useFlag('override-checkout-catering-minimum');
  const isOrderingEnabled = useFlag('enable-ordering');
  const { isStoreAvailable, store } = useStoreContext();

  const offers = useOffersContext();

  const {
    cartVersion,
    calculatedCartTotal,
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    serviceMode: orderServiceMode,
    selectServiceMode: selectOrderServiceMode,
    unavailableCartEntries,
  } = useOrderContext();

  const { logChangeServiceMode } = useCRMEventsContext();

  const { cartEntries: globalCartEntries, lastPriceCheck, updatePriceTimeStamp } = useCartValues();

  useCheckoutEventLogging(globalCartEntries);

  const {
    addItemToCart,
    removeItemFromCart: removeFromCart,
    updateCartItemsQuantity: updateItemsQuantity,
    swapCartItems: swapItems,
  } = useCartActions();

  const dispatch = useAppDispatch();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);
  const loyaltyAppliedDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const selectedOfferSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);
  const isPricingRewardApplication = useAppSelector(
    selectors.loyalty.selectIsPricingRewardApplication
  );
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const discountAppliedCmsOffers = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);

  const [continueButtonDisabled, setContinueButtonDisabled] = useState(false);
  const [cartCateringPriceTooLow, setCartCateringPriceTooLow] = useState(false);
  const [cardSuccessfullyAdded, setCardSuccessfullyAdded] = useState(false);
  const [shouldSkipPriceOrder, setShouldSkipPriceOrder] = useState(false);
  const [shouldSkipPriceOrderStatusPolling, setShouldSkipPriceOrderStatusPolling] = useState(false);
  const [serviceMode, setServiceMode] = useState(orderServiceMode);
  const [isCommittingOrder, setIsCommittingOrder] = useState(false);
  const [_areDeliveryDetailsValid, setAreDeliveryDetailsValid] = useState(true);
  const areDeliveryDetailsValid =
    _areDeliveryDetailsValid &&
    (serviceMode === ServiceMode.DELIVERY ? Boolean(orderPhoneNumber) : true);
  // Keep local ref of the serviceMode to properly update OrderContext on unmount
  // The ref update is synchronous, while the state update will only occur after re-render
  const contextServiceModeRef = useRef<ServiceMode>(orderServiceMode);

  const deliveryAddressBoxRef = useBoxRef(deliveryAddress);
  const refreshCurrentUserBoxRef = useBoxRef(refreshCurrentUser);

  const selectServiceMode = useCallback(
    (selectedServiceMode: ServiceMode) => {
      logChangeServiceMode({
        newServiceMode: selectedServiceMode,
        oldServiceMode: contextServiceModeRef.current,
      });
      //
      setServiceMode(selectedServiceMode);
      contextServiceModeRef.current = selectedServiceMode;
    },
    [setServiceMode, logChangeServiceMode]
  );

  const cartEntries: ICartEntry[] = useMemo(() => {
    // TODO: clean up cartEntries
    // Create the cart entry from the selected offer cart entry
    const offerMenuCartEntry = offers.selectedOfferCartEntry
      ? [
          {
            ...offers.selectedOfferCartEntry,
            price: offers.selectedOfferPrice,
            offerVendorConfigs: offers.selectedOffer && offers.selectedOffer.vendorConfigs,
            type: mapEntryTypeToOfferType(offers.selectedOfferCartEntry.type),
          },
        ]
      : [];
    // Create the cart entry from the selected discount offer
    // This offer will be available if the cart is not empty
    const selectedOffer = loyaltyAppliedDiscountOffer || offers.selectedOffer;

    // @ts-expect-error TS(2322) FIXME: ICartEntry does not support empty vendorConfigs
    const offerDiscountCartEntry: ICartEntry[] =
      globalCartEntries.length &&
      selectedOffer &&
      (isDiscountOffer(selectedOffer) || isLoyaltyOfferV2(selectedOffer?.__typename!))
        ? [
            {
              ...createCartEntry({ item: selectedOffer }),
              cartId: 'discount-offer',
              vendorConfigs: undefined,
              offerVendorConfigs: selectedOffer.vendorConfigs,
            },
          ]
        : [];

    const appliedOffersMap = appliedOffers.reduce((acc, offer) => {
      acc[offer.cartId || ''] = offer;
      return acc;
    }, {});

    // Updating entry type if its part of an applied offer
    const updatedCartEntries = (globalCartEntries || []).map((entry: ICartEntry) =>
      appliedOffersMap[entry.cartId]
        ? { ...entry, type: mapEntryTypeToOfferType(entry.type) }
        : entry
    );

    // Cart entries with offers
    return updatedCartEntries.concat(offerMenuCartEntry, offerDiscountCartEntry);
  }, [
    appliedOffers,
    loyaltyAppliedDiscountOffer,
    globalCartEntries,
    offers.selectedOffer,
    offers.selectedOfferCartEntry,
    offers.selectedOfferPrice,
  ]);

  const rewardsApplied = useSanitizedRewards({ cartEntries });

  const {
    priceOrder,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: { error: priceMutationError },
    serverOrder,
    loading: serverOrderLoading,
    serverOrderError,
  } = usePriceOrder({
    cartEntries,
    skipOrderStatusPolling: shouldSkipPriceOrderStatusPolling,
  });

  useEffect(() => {
    const deliveryAddressSnapshot = deliveryAddressBoxRef.current;
    const refreshUser = refreshCurrentUserBoxRef.current;
    return () => {
      if (deliveryAddressSnapshot?.shouldSave) {
        refreshUser();
      }
    };
  }, [deliveryAddressBoxRef, refreshCurrentUserBoxRef]);

  useEffect(() => {
    // Update OrderContext on unmount since Cart components should use serviceMode from CartContext
    // This significantly improves perceived performance when switching between serviceMode options
    return () => {
      if (contextServiceModeRef.current !== orderServiceMode) {
        selectOrderServiceMode(contextServiceModeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Ensure the service mode is being reset if it's Delivery
    // This happens in React Nav when changing stores from the Cart between Pick Up/Delivery that may result in a stale state
    const isDeliveryOrder =
      isDelivery(orderServiceMode) || isDelivery(contextServiceModeRef.current);
    if (isDeliveryOrder && contextServiceModeRef.current !== orderServiceMode) {
      selectServiceMode(orderServiceMode);
    }
  }, [orderServiceMode, selectServiceMode]);

  // for confirming item removal
  const confirmRemoveItemFromCart = useCallback(
    (cartId: string) => {
      setContinueButtonDisabled(true);
      const entryToRemove = globalCartEntries.find((item: ICartEntry) => cartId === item.cartId);
      entryToRemove && removeFromCart(entryToRemove);
    },
    [globalCartEntries, removeFromCart]
  );

  const tryPricing = useCallback(
    (overrideOrderParams?: ITryPricingOverrides) => {
      /**
       * Sample barcode composition looks like:
       * 046300...|F78B55B3...|13FF07A7DC0C...|MA|DT:TO|PT:TC
       * where MA means mobile app, DT is the dining type and PT is the payment type
       * https://rbictg.atlassian.net/browse/CBA-12104
       */
      const isCartTotalNegative = calculatedCartTotal < 0;

      // Skip any pricing when an order commit is in progress to prevent
      // infinite poll and allow the commit to complete, navigation handled on cart page
      if (
        !isStoreAvailable ||
        !user ||
        redeemReward ||
        isCartTotalNegative ||
        cartEntries.length === 0 ||
        isCommittingOrder
      ) {
        return;
      }

      const validAppliedOffers = appliedOffers.map(({ id, cartId, swap, type, cmsId }) => ({
        id,
        cartId,
        swap,
        type,
        sanityId: cmsId,
      }));

      const customerName = null;
      const orderParams = {
        rewardsApplied,
        cartVersion,
        customerLocale,
        customerName,
        deliveryAddress,
        orderPhoneNumber,
        deliveryInstructions,
        appliedOffers: validAppliedOffers,
        requestedAmountCents: calculatedCartTotal,
        serviceMode,
        store,
        isStoreAvailable,
        incentiveSelections,
        ...overrideOrderParams,
      };

      void priceOrder(orderParams, user);
      updatePriceTimeStamp();

      if (isPricingRewardApplication) {
        dispatch(actions.loyalty.setIsPricingRewardApplication(false));
      }
    },
    [
      cartEntries,
      calculatedCartTotal,
      isStoreAvailable,
      user,
      appliedOffers,
      rewardsApplied,
      cartVersion,
      customerLocale,
      deliveryAddress,
      orderPhoneNumber,
      deliveryInstructions,
      serviceMode,
      store,
      incentiveSelections,
      priceOrder,
      isPricingRewardApplication,
      dispatch,
      updatePriceTimeStamp,
      isCommittingOrder,
    ]
  );

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const cartPriceIntervalCheck = () => {
    const now = Date.now();
    const timeDelta = now - lastPriceCheck;
    const fifteenMinutes = 15 * 60 * 1000;
    if (timeDelta >= fifteenMinutes) {
      !isCommittingOrder && tryPricing();
    }
  };

  useEffect(() => {
    cartPriceIntervalCheck();
    intervalRef.current = setInterval(cartPriceIntervalCheck, 60 * 1000);
    return () => {
      clearInterval(intervalRef.current || undefined);
    };
  }, [lastPriceCheck]);

  const getRemoveItemWithOfferText = (item: ICartEntry) =>
    shouldOfferBeRemoved(
      globalCartEntries,
      item,
      offers,
      loyaltyEnabled,
      appliedLoyaltyRewards,
      discountAppliedCmsOffers
    ) || shouldOfferV2BeRemoved(item, selectedOfferSelections)
      ? formatMessage({ id: 'removeItemFromCartOfferIncentiveText' })
      : '';

  const removeItemMessage = (item: ICartEntry) => {
    return item
      ? formatMessage(
          { id: 'removeItemFromCart' },
          {
            item: item.name,
            offerText: getRemoveItemWithOfferText(item),
          }
        )
      : '';
  };
  const pricedAndAvailable = useMemo(() => {
    return priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  }, [priceMutationError, priceOrderSuccess, unavailableCartEntries.length]);

  const selectedDonationCartEntry = useMemo(
    () => cartEntries.find((cartEntry: ICartEntry) => cartEntry.isDonation),
    [cartEntries]
  );

  useAlertOrderCateringMinimum({
    calculateCartTotal: useCallback(() => calculatedCartTotal, [calculatedCartTotal]),
    checkoutCateringPriceMinimum,
    isCatering: isCatering(orderServiceMode),
    setCartCateringPriceTooLow,
    cartEntries,
  });

  const hasPriceError = !!priceMutationError || priceOrderFailure;
  const isCartTotalNegative = calculatedCartTotal < 0;
  const isRateLimitingError = graphQLErrorsIncludeExtensionCode(
    priceMutationError instanceof ApolloError ? priceMutationError.graphQLErrors : [],
    GraphQLErrorCodes.TOO_MANY_REQUESTS
  );
  const showCartErrorModal = useMemo(
    // should be in order context
    () =>
      hasPriceError &&
      !isRateLimitingError &&
      !unavailableCartEntries.length &&
      !isCartTotalNegative &&
      !shouldSkipPriceOrder,
    [
      hasPriceError,
      isRateLimitingError,
      isCartTotalNegative,
      shouldSkipPriceOrder,
      unavailableCartEntries,
    ]
  );

  const [showRateLimitModal, setShowRateLimitModal] = useState(false);

  useEffect(() => {
    if (isRateLimitingError && !showRateLimitModal) {
      setShowRateLimitModal(true);

      const modalTimer = setTimeout(() => {
        setShowRateLimitModal(false);
      }, RATE_LIMIT_TIMEOUT);

      return () => clearTimeout(modalTimer);
    }
    return undefined;
  }, [isRateLimitingError]);

  return (
    <CartContext.Provider
      value={{
        cartEntries,
        addItemToCart,
        confirmRemoveItemFromCart,
        removeItemMessage,
        removeFromCart,
        getRemoveItemWithOfferText,
        continueButtonDisabled,
        setContinueButtonDisabled,
        updateItemsQuantity,
        serverOrder,
        serverOrderLoading,
        serverOrderError: Boolean(serverOrderError),
        tryPricing,
        isOrderingEnabled,
        cardSuccessfullyAdded,
        setCardSuccessfullyAdded,
        orderStatus,
        priceOrderFailure,
        priceOrderSuccess,
        priceMutationError,
        pricedAndAvailable,
        unavailableCartEntries,
        serviceMode,
        selectServiceMode,
        shouldSkipPriceOrder,
        setShouldSkipPriceOrder,
        shouldSkipPriceOrderStatusPolling,
        setShouldSkipPriceOrderStatusPolling,
        selectedDonationCartEntry,
        swapItems,
        areDeliveryDetailsValid,
        setAreDeliveryDetailsValid,
        calculatedCartTotal,
        showCartErrorModal,
        cartCateringPriceTooLow,
        showRateLimitModal,
        rewardsApplied,
        isCommittingOrder,
        setIsCommittingOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);
